import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import EmptyState from './EmptyState';
import type { CapitalHistoryResponse } from '../../types/analytics';

interface Props {
  data: CapitalHistoryResponse | null;
  /** Fixed pixel height. Ignored when `fill` is true. */
  height?: number;
  /** When true, the chart fills its parent (use inside `flex-1` parents). */
  fill?: boolean;
}

const CapitalHistoryChart: React.FC<Props> = ({ data, height = 200, fill = false }) => {
  const hasPoints = !!data?.points?.length;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${fill ? 'h-full flex flex-col' : ''}`}>
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Capital Deployed
        </p>
      </div>
      <div className={`px-5 pt-4 pb-2 ${fill ? 'flex-1 min-h-0' : ''}`}>
        {hasPoints ? (
          <ResponsiveContainer width="100%" height={fill ? '100%' : height}>
            <LineChart data={data!.points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.18} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
              <YAxis
                tick={{ fontSize: 11 }}
                width={70}
                tickFormatter={(v) => `₹${Math.round(v).toLocaleString('en-IN')}`}
              />
              <Tooltip
                formatter={((v: any) => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`) as any}
                contentStyle={{ borderRadius: 6, fontSize: 12 }}
              />
              {/* Step interpolation since capital is constant between change points. */}
              <Line
                type="stepAfter"
                dataKey="capital"
                stroke="#6366f1"
                strokeWidth={2}
                dot={false}
                name="Capital"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState message="No capital history yet — analytics will populate after the next nightly run." />
        )}
      </div>
    </div>
  );
};

export default CapitalHistoryChart;
